<i18n lang="yaml">
  ru:
    ActionSidebarAvatarTitle: "Аватар"
    ActionSidebarAvatarDescription: "не более"
  en:
    ActionSidebarAvatarTitle: "Avatar"
    ActionSidebarAvatarDescription: "no more"
</i18n>

<template>
  <PWAsideBodyItem>
    <template #header>
      <div :class="$style.actionSidebarAvatar">
        <div :class="$style.actionSidebarAvatarToggle">
          <PWSwitcher
            v-if="!withoutToggle"
            v-model="avatarEnabledLocal"
          />
          <span
            :class="$style.actionSidebarAvatarLabel"
            @click="avatarEnabledLocal = !avatarEnabledLocal"
          >
            {{ $t('ActionSidebarAvatarTitle') }}
          </span>
        </div>
        <span
          v-if="avatarEnabledLocal"
          :class="$style.actionSidebarAvatarDescription"
        >
          png, jpg, gif {{ $t('ActionSidebarAvatarDescription') }} 5{{ $t('mb') }}
        </span>
      </div>
    </template>
    <template
      v-if="avatarEnabledLocal || withoutToggle"
      #default
    >
      <div :class="$style.actionSidebarAvatarVariants">
        <ActionSidebarAvatarItem
          custom
          :src="customPreview"
          :is-active="selectedAvatar === 'custom'"
          @click="handleClickPickAvatar"
          @handleRemove="handleRemoveAvatar"
        />
        <ActionSidebarAvatarItem
          v-for="(preview, idx) in previews"
          :key="idx"
          :src="preview"
          :is-active="selectedAvatar === idx"
          @click="handleClickDefaultAvatar(idx)"
        />
      </div>

      <input
        v-show="false"
        ref="input"
        type="file"
        accept="image/jpeg, image/png, image/gif"
        @change="handleChange"
      >

      <PWCropper ref="cropper" />
    </template>
  </PWAsideBodyItem>
</template>

<script>
/**
 * UI
 */
import PWAsideBodyItem from '@/components/_ui/Sidebars/PWAsideBodyItem.vue'
import PWSwitcher from '@/components/_ui/PWSwitcher.vue'
import ActionSidebarAvatarItem from '@/components/Actions/ActionSidebar/ActionSidebarAvatar/ActionSidebarAvatarItem.vue'
import PWCropper from '@/components/_ui/PWCropper.vue'
/**
 * Static
 */
import DefaultAvatar1 from '@/assets/default-images/avatars/christina.png'
import DefaultAvatar2 from '@/assets/default-images/avatars/denis.png'
import DefaultAvatar3 from '@/assets/default-images/avatars/lia.png'
import DefaultAvatar4 from '@/assets/default-images/avatars/roman.png'

export default {
  components: {
    PWAsideBodyItem,
    PWSwitcher,
    ActionSidebarAvatarItem,
    PWCropper
  },
  props: {
    avatarEnabled: {
      type: Boolean,
      default: false
    },
    withoutToggle: {
      type: Boolean,
      default: false
    },
    avatar: {
      type: [
        Object,
        process.client ? window.File : Object
      ],
      default: null
    }
  },
  data: () => ({
    defaultFilesBuffers: [],
    previews: [null, null, null, null],
    selectedAvatar: 0,
    selectedFile: null,
    customPreview: null
  }),
  computed: {
    avatarEnabledLocal: {
      get () { return this.avatarEnabled },
      set (val) { this.$emit('update-avatarEnabled', val) }
    },
    avatarLocal: {
      get () { return this.avatar },
      set (val) { this.$emit('update-avatar', val) }
    }
  },
  async mounted () {
    if (!this.defaultFilesBuffers.length) {
      await this.initDefaultAvatars()
    }
  },
  methods: {
    checkFileSize (file) {
      return file.size > 5000000 // 5mb
    },

    async handleChange (ev) {
      const file = ev.target.files[0]
      if (file && !this.checkFileSize(file)) {
        const croppedFile = await this.$refs.cropper.open({ file })
        this.avatarLocal = croppedFile
        if (croppedFile) {
          this.selectedAvatar = 'custom'
          this.selectedFile = croppedFile
          this.customPreview = URL.createObjectURL(croppedFile)
        }
      }
    },

    async initDefaultAvatars () {
      this.defaultFilesBuffers = []
      // import via fs
      const promises = [
        fetch(DefaultAvatar1),
        fetch(DefaultAvatar2),
        fetch(DefaultAvatar3),
        fetch(DefaultAvatar4)
      ]
      const results = await Promise.all(promises)
      // decode to blob
      const waitBlob = results.map(r => r.blob())
      const resultsBlob = await Promise.all(waitBlob)
      // save blob in buffer
      const downloadToBrowser = resultsBlob.map((blob, idx) => new File([blob], `default-avatar-${idx + 1}.png`, { type: 'image/png' }))
      this.defaultFilesBuffers = await Promise.all(downloadToBrowser)
      this.previews = this.defaultFilesBuffers.map(file => URL.createObjectURL(file))
      this.avatarLocal = this.defaultFilesBuffers[0]
    },

    handleClickDefaultAvatar (idx) {
      this.selectedAvatar = idx
      this.avatarLocal = this.defaultFilesBuffers[idx]
    },

    handleClickPickAvatar () {
      if (!this.selectedFile) {
        this.$refs.input.click()
      } else {
        this.avatarLocal = this.selectedFile
        this.selectedAvatar = 'custom'
      }
    },

    handleRemoveAvatar () {
      this.avatarLocal = this.defaultFilesBuffers[0]
      this.selectedAvatar = 0
      this.selectedFile = null
      this.customPreview = null
    }
  }
}
</script>

<style module lang="scss">
@use '@/assets/scss/mixins';

.actionSidebarAvatar {
  display: flex;
  width: 100%;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
}

.actionSidebarAvatarToggle {
  display: inline-flex;
  gap: 16px;
  align-items: center;
}

.actionSidebarAvatarLabel {
  user-select: none;
  cursor: pointer;
}

.actionSidebarAvatarDescription {
  color: var(--grey-400);

  @include mixins.body2;
}

.actionSidebarAvatarVariants {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}
</style>
